import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRightLeft, faSpinner, faCheck, faCircleExclamation, faDumbbell,
  faShieldHalved, faLock, faStar, faXmark, faUser,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import sharedStyles from "./SelfWorkoutModal.module.css";
import styles from "./MigrateWorkoutModal.module.css";
import ModalHeader from "./ModalHeader";
import DayCard from "./DayCard";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

/**
 * MigrateWorkoutModal - fluxo de MIGRAÇÃO/TRANSCRIÇÃO: o aluno já treina e só
 * quer trazer o treino que já faz pro TreinoNexo, sem que a IA redesenhe nada
 * (fidelidade > "correção" - ver app/prompts/workout_transcribe.md e
 * docs/07-issues.md). Espelha a estrutura de SelfWorkoutModal.jsx (mesmo
 * padrão de fases, ModalHeader, useBodyScrollLock, createPortal, guarda de
 * beforeunload, aviso de descarte) mas com um formulário de UMA tela (texto
 * livre + perfil opcional) em vez do wizard de várias etapas - aqui não há
 * decisões de programação pro aluno tomar, só relato.
 *
 * Reaproveita as classes visuais de SelfWorkoutModal.module.css (overlay,
 * modal, header, body, footer, botões, campos, cards de opção, estado de
 * geração, rationale, sucesso) pra não duplicar ~400 linhas de CSS e evitar
 * drift entre os dois fluxos - MigrateWorkoutModal.module.css traz só o que
 * é específico daqui (textarea de captura, link de fallback, toggle de perfil).
 */

const MIN_SELF_SERVICE_AGE = 18;

function calculateAge(birthDateStr) {
  if (!birthDateStr) return null;
  const birth = new Date(birthDateStr);
  if (Number.isNaN(birth.getTime())) return null;
  return Math.floor((Date.now() - birth.getTime()) / (365.25 * 86400000));
}

const fieldStyle = {
  width: "100%", boxSizing: "border-box",
  background: "var(--surface-secondary)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 8, padding: "8px 10px",
  color: "var(--text-primary)", fontSize: 13,
  colorScheme: "dark", outline: "none",
};

const MONTHS_PT = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"
];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 121 }, (_, i) => CURRENT_YEAR - i);

const ROUTINE_TEXT_MAX = 2000;
const ROUTINE_TEXT_MIN = 15;

const ROUTINE_EXAMPLE =
  "Ex: Treino A (peito/tríceps): supino reto 4x10, crucifixo 3x12, tríceps corda 3x15.\n" +
  "Treino B (costas/bíceps): puxada frente 4x10, remada curvada 3x10, rosca direta 3x12.\n" +
  "Faço 4x por semana, alternando A e B, com 1 dia de descanso entre eles.";

const GENERATING_MESSAGES = [
  "Lendo o treino que você descreveu…",
  "Identificando os exercícios no catálogo…",
  "Organizando os dias e séries…",
  "Conferindo consistência do treino…",
  "Quase lá…",
];

const DEFAULT_FORM = { weight_kg: "", height_cm: "", birth_date: "", gender: "", activity_level: "" };

export default function MigrateWorkoutModal({ open, onClose, onSuccess, user, onOpenSupport, onOpenPayments, onSwitchToBuilder }) {
  useBodyScrollLock(open);
  const [phase, setPhase] = useState("capture"); // capture | generating | gen-error | preview | success
  const [routineText, setRoutineText] = useState("");
  const [showProfileFields, setShowProfileFields] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [freshUser, setFreshUser] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [discardWarning, setDiscardWarning] = useState(false);
  const [genMsgIdx, setGenMsgIdx] = useState(0);
  const initParts = (() => {
    const bd = user?.birth_date || "";
    const [y, m, d] = bd ? bd.split("-") : ["", "", ""];
    return { y: y || "", m: m || "", d: d || "" };
  })();
  const [birthY, setBirthY] = useState(initParts.y);
  const [birthM, setBirthM] = useState(initParts.m);
  const [birthD, setBirthD] = useState(initParts.d);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open) { fetchedRef.current = false; return; }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    api.get("/users/me").then(r => setFreshUser(r.data)).catch(() => {});
    api.get("/ai/self-workout/last-usage").then(r => setUsageData(r.data)).catch(() => {});
  }, [open]);

  // Rotaciona mensagens durante a fase de organização
  useEffect(() => {
    if (phase !== "generating") return;
    setGenMsgIdx(0);
    const iv = setInterval(() => setGenMsgIdx(i => Math.min(i + 1, GENERATING_MESSAGES.length - 1)), 3500);
    return () => clearInterval(iv);
  }, [phase]);

  // Mesma guarda de SelfWorkoutModal: evita fechar a aba/F5 no meio da chamada
  // da IA, onde o treino pode não ficar gravado.
  useEffect(() => {
    if (phase !== "generating") return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [phase]);

  const profile = freshUser ?? user;

  const effectiveBirthDate = profile?.birth_date || form.birth_date || null;
  const currentAge = calculateAge(effectiveBirthDate);
  const isUnderAge = currentAge != null && currentAge < MIN_SELF_SERVICE_AGE;
  const showAgeBlock = Boolean(profile?.birth_date) && isUnderAge;

  const needsBirthDate = !profile?.birth_date;
  const needsGender = !profile?.gender;
  const needsHeightCm = !profile?.height_cm;
  const needsActivityLevel = !profile?.activity_level;

  function reset() {
    setPhase("capture");
    setRoutineText("");
    setForm(DEFAULT_FORM);
    setError("");
    setPreview(null);
    setConfirming(false);
    setSuccessData(null);
    setUsageData(null);
    setShowProfileFields(false);
  }

  function handleClose() {
    if (phase === "generating" || confirming) return;
    if (preview !== null && phase !== "success") { setDiscardWarning(true); return; }
    reset();
    onClose();
  }

  function profilePayload() {
    return {
      birth_date: needsBirthDate ? (form.birth_date || null) : null,
      gender: needsGender ? (form.gender || null) : null,
      height_cm: needsHeightCm ? (Number(form.height_cm) || null) : null,
      weight_kg: Number(form.weight_kg) || null,
      activity_level: needsActivityLevel ? (form.activity_level || null) : null,
    };
  }

  const routineValid = routineText.trim().length >= ROUTINE_TEXT_MIN;
  const birthDateBlocking = needsBirthDate && /^\d{4}-\d{2}-\d{2}$/.test(form.birth_date) && isUnderAge;

  async function handleSubmit() {
    if (!routineValid || birthDateBlocking) return;
    setError("");
    setPhase("generating");

    const body = {
      routine_text: routineText.trim(),
      height_cm: needsHeightCm ? (Number(form.height_cm) || undefined) : undefined,
      weight_kg: Number(form.weight_kg) || undefined,
      birth_date: needsBirthDate ? (form.birth_date || undefined) : undefined,
      gender: needsGender ? (form.gender || undefined) : undefined,
      activity_level: needsActivityLevel ? (form.activity_level || undefined) : undefined,
      equipment: [],
    };

    try {
      const res = await api.post("/ai/self-workout/transcribe/preview", body);
      const data = res.data;
      setPreview({
        days: data.days,
        rationale: data.rationale,
        rationale_student: data.rationale_student || null,
        ai_usage_log_id: data.ai_usage_log_id ?? null,
        sanity_flags: data.sanity_flags || [],
      });
      setPhase("preview");
    } catch (err) {
      const _detail = err?.response?.data?.detail;
      if (typeof _detail === "object" && _detail?.error === "renovation_limit_reached") {
        setError(_detail.message || "Limite de renovações atingido.");
        setPhase("renovation-limit");
      } else {
        const _msg = typeof _detail === "string" ? _detail : (_detail?.message || "Não conseguimos estruturar seu treino. Tente novamente.");
        setError(_msg);
        setPhase("gen-error");
      }
    }
  }

  async function handleConfirm() {
    if (!preview?.days) return;
    setError("");
    setConfirming(true);
    try {
      const res = await api.post("/ai/self-workout/confirm", {
        days: preview.days,
        rationale: preview.rationale || null,
        rationale_student: preview.rationale_student || null,
        ai_usage_log_id: preview.ai_usage_log_id ?? null,
        ...profilePayload(),
      });
      let freshProfile = null;
      try { freshProfile = (await api.get("/users/me")).data; } catch (_) {}
      setSuccessData({ ...res.data, freshProfile });
      setPhase("success");
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar treino. Tente novamente.");
    } finally {
      setConfirming(false);
    }
  }

  if (!open) return null;

  // Bloqueio de limite de renovações (Free plan) - mesmo contador de "Gerar Treino (Self)"
  if (usageData && usageData.has_reached_renovation_limit) {
    const renovationLimitContent = (
      <div className={sharedStyles.overlay} onClick={handleClose}>
        <div className={sharedStyles.modal} onClick={e => e.stopPropagation()}>
          <ModalHeader onClose={handleClose} disabled={false} />
          <div className={sharedStyles.header}>
            <div className={sharedStyles.headerLeft}>
              <div className={sharedStyles.headerIcon}>
                <FontAwesomeIcon icon={faStar} />
              </div>
              <div>
                <h2 className={sharedStyles.title}>Limite de renovações atingido</h2>
                <p className={sharedStyles.sub}>Faça upgrade para continuar</p>
              </div>
            </div>
          </div>
          <div className={sharedStyles.body}>
            <div className={sharedStyles.generatingWrap}>
              <p className={sharedStyles.generatingText}>
                Você já usou suas renovações de treino disponíveis no plano Free.
              </p>
              <div className={sharedStyles.featureList} style={{ marginTop: "16px" }}>
                <div className={`${sharedStyles.featureItem} ${sharedStyles.featureItemActive}`}>
                  <FontAwesomeIcon icon={faCheck} className={sharedStyles.featureIcon} />
                  <span>Uma geração/migração inicial</span>
                </div>
                <div className={`${sharedStyles.featureItem} ${sharedStyles.featureItemActive}`}>
                  <FontAwesomeIcon icon={faCheck} className={sharedStyles.featureIcon} />
                  <span>Uma renovação após 90 dias</span>
                </div>
                <div className={`${sharedStyles.featureItem} ${sharedStyles.featureItemInactive}`}>
                  <FontAwesomeIcon icon={faXmark} className={sharedStyles.featureIcon} />
                  <span>Próximas renovações</span>
                </div>
              </div>
              <p className={sharedStyles.generatingHint}>
                Faça upgrade para o <strong>Plus AI</strong> e desbloqueie renovações ilimitadas,
                análise de progresso, dieta personalizada e muito mais.
              </p>
            </div>
          </div>
          <div className={sharedStyles.footer}>
            <button type="button" className={sharedStyles.btnSecondary} onClick={handleClose}>
              Talvez depois
            </button>
            <button
              type="button"
              className={sharedStyles.btnPrimary}
              onClick={() => {
                handleClose();
                if (onOpenPayments) setTimeout(() => onOpenPayments(), 200);
              }}
            >
              <FontAwesomeIcon icon={faStar} /> Upgrade para Plus AI
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(renovationLimitContent, document.body);
  }

  // Bloqueio de cooldown: aluno já gerou/migrou treino nos últimos 90 dias
  if (usageData && !usageData.can_generate) {
    const nextDate = usageData.next_allowed_at
      ? new Date(usageData.next_allowed_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
      : null;
    const cooldownContent = (
      <div className={sharedStyles.overlay} onClick={handleClose}>
        <div className={sharedStyles.modal} onClick={e => e.stopPropagation()}>
          <ModalHeader onClose={handleClose} disabled={false} />
          <div className={sharedStyles.header}>
            <div className={sharedStyles.headerLeft}>
              <div className={sharedStyles.headerIcon}>
                <FontAwesomeIcon icon={faLock} />
              </div>
              <div>
                <h2 className={sharedStyles.title}>Treino já gerado/migrado</h2>
                <p className={sharedStyles.sub}>Próxima geração disponível em breve</p>
              </div>
            </div>
          </div>
          <div className={sharedStyles.body}>
            <div className={sharedStyles.generatingWrap}>
              <p className={sharedStyles.generatingText}>
                Você já usou sua geração/migração de treino com IA. Para manter a qualidade e a progressão,
                permitimos <strong>1 a cada 3 meses</strong>.
              </p>
              {nextDate && (
                <p className={sharedStyles.generatingHint}>
                  Sua próxima geração/migração estará disponível a partir de{" "}
                  <strong>{nextDate}</strong>.
                </p>
              )}
              <p className={sharedStyles.generatingHint}>
                Enquanto isso, siga o treino atual - a consistência é o que gera resultado.
                Se precisar de ajustes, peça orientação a um professor.
              </p>
            </div>
          </div>
          <div className={sharedStyles.footer}>
            <button type="button" className={sharedStyles.btnPrimary} onClick={handleClose}>
              Entendi
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(cooldownContent, document.body);
  }

  if (phase === "renovation-limit") {
    const renovationLimitContent = (
      <div className={sharedStyles.overlay} onClick={handleClose}>
        <div className={sharedStyles.modal} onClick={e => e.stopPropagation()}>
          <ModalHeader onClose={handleClose} disabled={false} />
          <div className={sharedStyles.header}>
            <div className={sharedStyles.headerLeft}>
              <div className={sharedStyles.headerIcon}>
                <FontAwesomeIcon icon={faStar} />
              </div>
              <div>
                <h2 className={sharedStyles.title}>Limite de renovações atingido</h2>
                <p className={sharedStyles.sub}>Faça upgrade para continuar</p>
              </div>
            </div>
          </div>
          <div className={sharedStyles.body}>
            <div className={sharedStyles.generatingWrap}>
              <p className={sharedStyles.generatingText}>
                Você já usou suas renovações de treino disponíveis no plano Free.
              </p>
              <p className={sharedStyles.generatingHint}>
                Faça upgrade para o <strong>Plus AI</strong> e desbloqueie renovações ilimitadas.
              </p>
            </div>
          </div>
          <div className={sharedStyles.footer}>
            <button type="button" className={sharedStyles.btnSecondary} onClick={handleClose}>
              Talvez depois
            </button>
            <button
              type="button"
              className={sharedStyles.btnPrimary}
              onClick={() => {
                handleClose();
                if (onOpenPayments) setTimeout(() => onOpenPayments(), 200);
              }}
            >
              <FontAwesomeIcon icon={faStar} /> Upgrade para Plus AI
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(renovationLimitContent, document.body);
  }

  // LGPD Art. 14 - mesmo bloqueio de SelfWorkoutModal: self-service exige maioridade
  if (showAgeBlock) {
    const blockedContent = (
      <div className={sharedStyles.overlay} onClick={handleClose}>
        <div className={sharedStyles.modal} onClick={e => e.stopPropagation()}>
          <ModalHeader onClose={handleClose} disabled={false} />
          <div className={sharedStyles.header}>
            <div className={sharedStyles.headerLeft}>
              <div className={sharedStyles.headerIcon}>
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <div>
                <h2 className={sharedStyles.title}>Self-service disponível pra maiores de 18</h2>
                <p className={sharedStyles.sub}>Migração de treino sem professor</p>
              </div>
            </div>
          </div>
          <div className={sharedStyles.body}>
            <div className={sharedStyles.generatingWrap}>
              <p className={sharedStyles.generatingText}>
                Por enquanto, migrar treino sozinho é liberado só pra maiores de 18 anos.
              </p>
              <p className={sharedStyles.generatingHint}>
                Peça pra um professor vincular sua conta - assim você tem acesso a treino
                com acompanhamento humano e IA, com um responsável por trás.
              </p>
            </div>
          </div>
          <div className={sharedStyles.footer}>
            <button type="button" className={sharedStyles.btnPrimary} onClick={handleClose}>
              Entendi
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(blockedContent, document.body);
  }

  const sanityByDay = new Map(
    (preview?.sanity_flags || [])
      .filter(f => f.day_index != null)
      .map(f => [f.day_index, f.message])
  );
  const generalFlags = (preview?.sanity_flags || []).filter(f => f.day_index == null);

  const headerTitle =
    phase === "capture"    ? "Migrar meu treino" :
    phase === "generating" ? "Organizando seu treino…" :
    phase === "gen-error"  ? "Não conseguimos estruturar" :
    phase === "preview"    ? "Confira seu treino" :
    phase === "success"    ? "Treino migrado!" : "";
  const headerSub =
    phase === "capture"    ? "Descreva o treino que você já faz" :
    phase === "generating" ? "Estamos organizando exatamente como você descreveu" :
    phase === "gen-error"  ? "Tente detalhar mais o seu relato" :
    phase === "preview"    ? "Confirme se ficou fiel ao que você descreveu" :
    phase === "success"    ? "Seus treinos já estão na sua ficha" : "";

  const modalContent = (
    <div className={sharedStyles.overlay} onClick={handleClose}>
      <div className={sharedStyles.modal} onClick={e => e.stopPropagation()}>

        <ModalHeader onClose={handleClose} disabled={phase === "generating" || confirming} />

        <div className={sharedStyles.header}>
          <div className={sharedStyles.headerLeft}>
            <div className={sharedStyles.headerIcon}>
              <FontAwesomeIcon icon={faRightLeft} />
            </div>
            <div>
              <h2 className={sharedStyles.title}>{headerTitle}</h2>
              <p className={sharedStyles.sub}>{headerSub}</p>
            </div>
          </div>
        </div>

        {/* ── CAPTURE ── */}
        {phase === "capture" && (
          <div className={sharedStyles.body}>
            <div className={sharedStyles.formGrid}>
              <p className={styles.captureText}>
                Já treina e não quer redesenhar nada? Conte pra gente exatamente o treino que você
                já faz - dias, exercícios, séries e repetições - e a gente só organiza na sua ficha,
                do jeito que está.
              </p>

              <div className={sharedStyles.field}>
                <label className={sharedStyles.label}>
                  Seu treino atual <span className={sharedStyles.required}>*</span>
                </label>
                <textarea
                  className={styles.textareaField}
                  placeholder={ROUTINE_EXAMPLE}
                  value={routineText}
                  maxLength={ROUTINE_TEXT_MAX}
                  onChange={e => setRoutineText(e.target.value)}
                  rows={9}
                />
                <span className={`${sharedStyles.charCount} ${routineText.length >= ROUTINE_TEXT_MAX ? sharedStyles.charCountLimit : ""}`}>
                  {routineText.length}/{ROUTINE_TEXT_MAX}
                </span>
              </div>

              {!showProfileFields && (
                <button type="button" className={styles.quickProfileToggle} onClick={() => setShowProfileFields(true)}>
                  <FontAwesomeIcon icon={faUser} /> Adicionar peso, altura e idade{" "}
                  <span className={sharedStyles.optional}>(opcional, ajuda a personalizar)</span>
                </button>
              )}

              {showProfileFields && (
                <div className={sharedStyles.profileSection}>
                  <p className={sharedStyles.profileSectionTitle}>Detalhes rápidos (opcional)</p>

                  <div className={sharedStyles.fieldRow}>
                    <div className={sharedStyles.field}>
                      <label className={sharedStyles.label}>Peso (kg)</label>
                      <input style={fieldStyle} type="number" min="30" max="250" step="0.1"
                        placeholder="Ex: 75"
                        value={form.weight_kg}
                        onChange={e => setForm(f => ({ ...f, weight_kg: e.target.value }))} />
                    </div>
                    {needsHeightCm && (
                      <div className={sharedStyles.field}>
                        <label className={sharedStyles.label}>Altura (cm)</label>
                        <input style={fieldStyle} type="number" min="100" max="250" step="1"
                          placeholder="Ex: 175"
                          value={form.height_cm}
                          onChange={e => setForm(f => ({ ...f, height_cm: e.target.value }))} />
                      </div>
                    )}
                  </div>

                  {needsBirthDate && (
                    <div className={sharedStyles.field}>
                      <label className={sharedStyles.label}>Data de nascimento</label>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <select style={{ ...fieldStyle, flex: "0 0 72px" }}
                          value={birthD}
                          onChange={e => {
                            const d = e.target.value;
                            setBirthD(d);
                            if (birthY && birthM && d) setForm(f => ({ ...f, birth_date: `${birthY}-${birthM}-${d}` }));
                            else setForm(f => ({ ...f, birth_date: "" }));
                          }}>
                          <option value="">Dia</option>
                          {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map(dd =>
                            <option key={dd} value={dd}>{parseInt(dd, 10)}</option>
                          )}
                        </select>
                        <select style={{ ...fieldStyle, flex: "1" }}
                          value={birthM}
                          onChange={e => {
                            const m = e.target.value;
                            setBirthM(m);
                            if (birthY && m && birthD) setForm(f => ({ ...f, birth_date: `${birthY}-${m}-${birthD}` }));
                            else setForm(f => ({ ...f, birth_date: "" }));
                          }}>
                          <option value="">Mês</option>
                          {MONTHS_PT.map((mn, i) => {
                            const val = String(i + 1).padStart(2, "0");
                            return <option key={val} value={val}>{mn}</option>;
                          })}
                        </select>
                        <select style={{ ...fieldStyle, flex: "0 0 88px" }}
                          value={birthY}
                          onChange={e => {
                            const y = e.target.value;
                            setBirthY(y);
                            if (y && birthM && birthD) setForm(f => ({ ...f, birth_date: `${y}-${birthM}-${birthD}` }));
                            else setForm(f => ({ ...f, birth_date: "" }));
                          }}>
                          <option value="">Ano</option>
                          {YEAR_OPTIONS.map(yr =>
                            <option key={yr} value={String(yr)}>{yr}</option>
                          )}
                        </select>
                      </div>
                      {birthDateBlocking && (
                        <div className={sharedStyles.error}>
                          O self-service com IA é liberado só pra maiores de 18 anos. Peça pra um
                          professor vincular sua conta pra ter acesso à plataforma.
                        </div>
                      )}
                    </div>
                  )}

                  {needsGender && (
                    <div className={sharedStyles.field}>
                      <label className={sharedStyles.label}>Gênero</label>
                      <div className={sharedStyles.optionGrid}>
                        {[
                          { v: "masculino", l: "Masculino" },
                          { v: "feminino", l: "Feminino" },
                          { v: "outro", l: "Outro" },
                        ].map(({ v, l }) => (
                          <button key={v} type="button"
                            className={`${sharedStyles.optionCard} ${form.gender === v ? sharedStyles.optionCardActive : ""}`}
                            onClick={() => setForm(f => ({ ...f, gender: v }))}>
                            <span className={sharedStyles.optionCardLabel}>{l}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {onSwitchToBuilder && (
                <button
                  type="button"
                  className={styles.fallbackLink}
                  onClick={() => { handleClose(); setTimeout(() => onSwitchToBuilder(), 200); }}
                >
                  Prefiro montar um treino novo com ajuda da IA →
                </button>
              )}
            </div>

            {error && <div className={sharedStyles.error}>{error}</div>}

            <div className={sharedStyles.footer}>
              <button type="button" className={sharedStyles.btnSecondary} onClick={handleClose}>
                Cancelar
              </button>
              <button type="button" className={sharedStyles.btnPrimary}
                onClick={handleSubmit}
                disabled={!routineValid || birthDateBlocking}>
                <FontAwesomeIcon icon={faRightLeft} /> Estruturar meu treino
              </button>
            </div>
          </div>
        )}

        {/* ── GENERATING ── */}
        {phase === "generating" && (
          <div className={sharedStyles.body}>
            <div className={sharedStyles.generatingWrap}>
              <FontAwesomeIcon icon={faSpinner} spin className={sharedStyles.generatingIcon} />
              <p className={sharedStyles.generatingText} key={genMsgIdx}>
                {GENERATING_MESSAGES[genMsgIdx]}
              </p>
              <p className={sharedStyles.generatingHint}>Mantendo fiel ao que você descreveu</p>

              <div
                role="alert"
                style={{
                  marginTop: 22, display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 16px", borderRadius: 14,
                  background: "rgba(245, 158, 11, 0.14)",
                  border: "1.5px solid rgba(245, 158, 11, 0.55)",
                  color: "var(--text)", maxWidth: 420, margin: "22px auto 0",
                }}
              >
                <FontAwesomeIcon icon={faCircleExclamation} style={{ color: "#f59e0b", fontSize: 22, flexShrink: 0 }} />
                <span style={{ fontSize: 14.5, lineHeight: 1.45, textAlign: "left" }}>
                  <strong>Não feche esta janela</strong> enquanto organizamos seu treino.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── GEN-ERROR ── */}
        {phase === "gen-error" && (
          <div className={sharedStyles.body}>
            <div className={sharedStyles.genError}>
              <div className={sharedStyles.genErrorIcon}>
                <FontAwesomeIcon icon={faCircleExclamation} />
              </div>
              <h3 className={sharedStyles.genErrorTitle}>Não conseguimos estruturar seu treino</h3>
              <p className={sharedStyles.genErrorMsg}>
                Talvez a descrição esteja muito resumida ou os exercícios não tenham sido reconhecidos.
                Tente detalhar mais - nomes dos exercícios, dias, séries e repetições.
              </p>
              {error && (
                <p className={sharedStyles.genErrorDetail}>{error}</p>
              )}
              <div className={sharedStyles.genErrorBtns}>
                <button
                  className={sharedStyles.genErrorRetry}
                  onClick={() => { setError(""); setPhase("capture"); }}
                >
                  Tentar novamente
                </button>
                {onOpenSupport && (
                  <button
                    className={sharedStyles.genErrorSupport}
                    onClick={() => { onClose(); setTimeout(() => onOpenSupport(), 200); }}
                  >
                    Dúvidas? Fale conosco
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── PREVIEW ── */}
        {phase === "preview" && preview && (
          <div className={sharedStyles.body}>
            <div className={sharedStyles.previewMeta}>
              <FontAwesomeIcon icon={faDumbbell} className={sharedStyles.previewMetaIcon} />
              <span><strong>{preview.days?.length} treino(s)</strong> organizados a partir do que você descreveu</span>
            </div>

            {(preview.rationale_student || preview.rationale) && (
              <div className={sharedStyles.rationaleBox}>
                <p className={sharedStyles.rationaleTitle}>Confirmação</p>
                <p className={sharedStyles.rationaleText}>{preview.rationale_student || preview.rationale}</p>
              </div>
            )}

            {generalFlags.map((f, i) => (
              <div key={i} className={styles.generalWarning}>
                <FontAwesomeIcon icon={faCircleExclamation} className={styles.generalWarningIcon} aria-hidden="true" />
                <span>{f.message}</span>
              </div>
            ))}

            <div className={sharedStyles.dayList}>
              {(preview.days || []).map((day, i) => (
                <DayCard key={i} day={day} index={i} warning={sanityByDay.get(i)} />
              ))}
            </div>

            <div className={sharedStyles.previewNote}>
              Ao confirmar, esse treino vira sua ficha ativa no TreinoNexo. Treinos anteriores serão substituídos.
            </div>

            {error && <div className={sharedStyles.error}>{error}</div>}

            <div className={sharedStyles.footer}>
              <button type="button" className={sharedStyles.btnSecondary}
                onClick={() => { setPhase("capture"); setError(""); }}
                disabled={confirming}>
                ← Ajustar
              </button>
              <button type="button" className={sharedStyles.btnPrimary} onClick={handleConfirm} disabled={confirming}>
                {confirming
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> Salvando...</>
                  : <><FontAwesomeIcon icon={faCheck} /> Confirmar treino</>
                }
              </button>
            </div>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {phase === "success" && (
          <div className={sharedStyles.body}>
            <div className={sharedStyles.successWrap}>
              <div className={sharedStyles.successIcon}><FontAwesomeIcon icon={faCheck} /></div>
              <p className={sharedStyles.successTitle}>{successData?.days_created ?? ""} treino(s) migrado(s) com sucesso!</p>
              <p className={sharedStyles.successSub}>Seu treino já está disponível na sua ficha, do jeito que você descreveu.</p>
              <button type="button" className={sharedStyles.btnPrimary}
                onClick={() => { onSuccess?.(successData?.freshProfile); reset(); onClose(); }}>
                Ver meus treinos
              </button>
            </div>
          </div>
        )}

      </div>

      {/* ── DISCARD WARNING ── */}
      {discardWarning && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
        >
          <div
            style={{ background: "var(--surface-primary,#0f1e36)", borderRadius: 14, padding: "2rem 1.75rem", maxWidth: 420, width: "100%", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>🔒</div>
            <p style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: "0.75rem", color: "var(--text-primary)" }}>
              Seu treino está esperando por você
            </p>
            <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginBottom: "1.5rem", lineHeight: 1.6 }}>
              Confirme antes de fechar - se sair agora, o treino organizado se perde e você precisa
              descrever tudo de novo.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {phase !== "preview" && preview && (
                <button
                  type="button"
                  onClick={() => { setDiscardWarning(false); setPhase("preview"); }}
                  style={{ padding: "0.65rem 2rem", borderRadius: 8, border: "none", background: "var(--accent,#1d9e75)", color: "var(--on-accent)", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Ver treino e confirmar
                </button>
              )}
              <button
                type="button"
                onClick={() => setDiscardWarning(false)}
                style={{ padding: "0.65rem 2rem", borderRadius: 8, border: "1.5px solid var(--border-0-15)", background: "transparent", color: "var(--text-primary)", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem" }}
              >
                Continuar editando
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}
